import { useContext, useEffect } from "react"

import { useShallow } from "zustand/react/shallow"

import { useDict } from "@api/dict/state"
import { useApplications } from "@api/applications/state"

import { useValue } from "@hooks/useValue"
import { useDelayedInput } from "@hooks/useDelayedInput"

import { IModalConfirm, ModalConfirm } from "@primitive/modal.confirm"

import { ModalApplicationsPromoUI } from "./ui"
import { IApplicationStatuses, IModalApplicationsPromoActions, IModalApplicationsPromoFilter, IModalApplicationsPromoUI } from "./types"

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"
import { ModalApplicationsPromoFilter } from "./ui.filter"
import { IApplicationsPromoFilter } from "@api/applications/state.types"


/** Контейнер окна - Список промо-заявки МАКПП */
export const ModalApplicationsPromoActions = ({ isOpen, handleButtonOk, handleButtonCancel }: IModalApplicationsPromoActions) => {
    const notify = useContext(NotificationContext)
    
    const filterIsOpen = useValue<boolean>(false)
    const filterIsActivate = useValue<boolean>(false)
    const filterApplicationStatuses = useValue<IApplicationStatuses[]>([])
    const filterIsArchiveListOnly = useValue<string>('')

    /** Пагинация - Изменение номера страницы */
    const paginationPageNumber = useValue<number>(1)

    /** Окно подтверждения */
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})

    // Хранилище страницы - Заявки
    const { 
        isLoadingPage, applicationsList, applicationsPageSize, applicationsTotalCount,
        actionGetApplicationsPromo, clearStateApplicationsPromo, 
        actionSendTelegramAppPromo, actionSetApplicationPromoArchive,
    } = useApplications(useShallow((state) => ({
        isLoadingPage: state.applicationsPromo.isLoading,

        applicationsList: state.applicationsPromo.items,
        applicationsPageSize: state.applicationsPromo.pageSize,
        applicationsTotalCount: state.applicationsPromo.totalCount,

        actionGetApplicationsPromo: state.actionGetApplicationsPromo,
        clearStateApplicationsPromo: state.clearStateApplicationsPromo,
        actionSendTelegramAppPromo: state.actionSendTelegramAppPromo,
        actionSetApplicationPromoArchive: state.actionSetApplicationPromoArchive,
    })))

    // Хранилище справочники
    const dictState = useDict(useShallow((state) => state))

    /** Загрузка первичных данных */
    const loadPrimaryData = (search: string) => {
        let applicationStatusesListSelected: string = ''

        if (filterApplicationStatuses.value.length > 0) {
            filterApplicationStatuses.value.forEach((item) => {
                if (item.isSelected) {
                    applicationStatusesListSelected += `'${item.name}',`
                }
            })
        }

        const filterData: IApplicationsPromoFilter = {
            applicationStatuses: applicationStatusesListSelected,
            isArchiveListOnly: filterIsArchiveListOnly.value,
        }

        actionGetApplicationsPromo(paginationPageNumber.value, search, filterData).then((res) => {
            if (res.status !== 200 && res.status !== 500) {
                notify && notifyOpen(res.message, 'error', 2000, notify)
            }
        })
    }

    /** Запуск загрузки данных через полсекунды */
    const { value: txtSearch, setValue: setTxtSearch } = useDelayedInput<string>("", 1000, (val) => loadPrimaryData(val))

    useEffect(() => {
        if (!isOpen) return

        dictState.actionGetApplicationStatuses().then((res) => {
            filterApplicationStatuses.setValue(
                res.map((item) => ({
                    id: item.applicationStatusID,
                    name: item.applicationStatusName,
                    isSelected: false
                }))
            )    
        })

        return
    }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return (() => {
            clearStateApplicationsPromo()

            setTxtSearch('')
            paginationPageNumber.setValue(1)

            filterIsOpen.setValue(false)
            filterIsActivate.setValue(false)
            filterApplicationStatuses.setValue([])
            filterIsArchiveListOnly.setValue('')
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isOpen) return

        loadPrimaryData(txtSearch)
    }, [isOpen, paginationPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps


    // ================================================================================

    /** Кнопка применить (очистить) в окне - Фильтр по заявкам */
    const handleApplyClearFilter = (filterType: 'apply' | 'clear') => {
        switch (filterType) {
            case 'apply': {
                filterIsActivate.setValue(true)
                loadPrimaryData(txtSearch)
                break
            }

            case 'clear': {
                filterIsActivate.setValue(false)
                filterApplicationStatuses.setValue(filterApplicationStatuses.value.map(status => ({...status, isSelected: false})))
                filterIsArchiveListOnly.setValue('')

                actionGetApplicationsPromo(paginationPageNumber.value, txtSearch)
                break
            }
        }
    }

    /** Отправка информации в telegram канал (ИКТ - Промо (МАКПП)) */
    const handleSendToTelegram = (applicationID: number) => {
        actionSendTelegramAppPromo(Number(applicationID)).then((res) => {
            if (res.status !== 200 && res.status !== 500) {
                notify && notifyOpen(res.message, 'error', 2000, notify)
            }
        })
    }

    /** Открытие/закрытие окна подтверждения */
    const handleOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, applicationID: number, actionType: 'toArchiveList', currentBlackList: number) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText, 

            handleButtonCancel() {
                modalConfirm.setValue({isOpen: false})
            }, 
            
            handleButtonOk() {
                switch (actionType) {
                    case 'toArchiveList': { // Отправка заявки в архив
                        actionSetApplicationPromoArchive(applicationID, `${currentBlackList === 0 ? 'add' : 'delete'}`).then((res) => {
                            if (res.status === 200) {
                                loadPrimaryData(txtSearch)
                                notify && notifyOpen(`${currentBlackList === 0 ? 'Заявка успешно отправлена в архив' : 'Заявка успешно удалена из архива'}`, 'success', 1500, notify)
                            }

                            if (res.status !== 200 && res.status !== 500) {
                                notify && notifyOpen(res.message, 'error', 2000, notify)
                            }
                        }).finally(() => {
                            modalConfirm.setValue({isOpen: false})
                        })
                        break
                    }
                }
            }
        })
    }


    // ================================================================================

    /** Свойства передаваемые в компоненту */
    const propsToComponent: IModalApplicationsPromoUI = {
        isOpen, isLoadingPage, paginationPageNumber,
        txtSearch, filterIsOpen, filterIsActivate,

        applicationsList, applicationsPageSize, applicationsTotalCount,
        
        setTxtSearch, loadPrimaryData,
        handleButtonCancel, handleApplyClearFilter, 
        handleSendToTelegram, handleOpenModalConfirm,
    }
    
    /** Свойства передаваемые в окно фильтра */
    const propsToFilter: IModalApplicationsPromoFilter = {
        isOpen: filterIsOpen.value, 

        filterApplicationStatuses: filterApplicationStatuses, 
        filterIsArchiveListOnly: filterIsArchiveListOnly,

        handleButtonCancel: () => filterIsOpen.setValue(false),
        handleApplyClearFilter: handleApplyClearFilter,
    }

    return <>
        <ModalApplicationsPromoUI {...propsToComponent} />
        <ModalApplicationsPromoFilter {...propsToFilter} />

        <ModalConfirm {...modalConfirm.value} />
    </>
}